import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';


const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    themeColor: '#3b82f6', // Default blue-500
    logoUrl: '',
    schoolName: 'PenSense LMS'
  });
  
  const [isLoadingTheme, setIsLoadingTheme] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const tenantEmail = user.tenantEmail || "PENSENSE_DEFAULT";
        const res = await axios.get(`${API_BASE_URL}/api/theme?tenantIdOverride=${tenantEmail}`);
        if (res.data) {
          const newTheme = {
            themeColor: res.data.themeColor || '#3b82f6',
            logoUrl: res.data.logoUrl || '',
            schoolName: res.data.schoolName || 'PenSense LMS'
          };
          setTheme(newTheme);
          applyCSSVariables(newTheme);
        }
      } catch (err) {
        console.error("Theme fetch failed:", err);
      } finally {
        setIsLoadingTheme(false);
      }
    };
    fetchTheme();
  }, []);

  // Dynamic PWA Manifest Branding
  useEffect(() => {
    if (isLoadingTheme || !theme.schoolName) return;

    // Only generate dynamic manifest if it's not the default PenSense
    if (theme.schoolName === 'PenSense LMS' && !theme.logoUrl) return;

    const generateManifest = () => {
      const user = JSON.parse(localStorage.getItem('user') || "{}");
      const manifest = {
        "id": "/?tenant=" + (user.tenantEmail || "default"),
        "short_name": theme.schoolName,
        "name": `${theme.schoolName} - Portal`,
        "icons": [
          {
            "src": theme.logoUrl || "/logo192.png",
            "sizes": "192x192",
            "type": "image/png"
          },
          {
            "src": theme.logoUrl || "/logo512.png",
            "sizes": "512x512",
            "type": "image/png",
            "purpose": "any maskable"
          }
        ],
        "start_url": "/",
        "scope": "/",
        "display": "standalone",
        "theme_color": theme.themeColor || "#020617",
        "background_color": "#020617",
        "orientation": "portrait"
      };

      const stringManifest = JSON.stringify(manifest);
      const blob = new Blob([stringManifest], {type: 'application/json'});
      const manifestURL = URL.createObjectURL(blob);
      
      const manifestLink = document.querySelector('#manifest-link');
      if (manifestLink) {
        manifestLink.setAttribute('href', manifestURL);
      }

      // Also update Apple Touch Icon for iOS specifically
      const appleIconLink = document.querySelector('#apple-touch-icon');
      if (appleIconLink && theme.logoUrl) {
        appleIconLink.setAttribute('href', theme.logoUrl);
      }
    };

    generateManifest();
  }, [theme, isLoadingTheme]);

  const applyCSSVariables = (t) => {
    const root = document.documentElement;
    if (t.themeColor) {
      // Very basic injection - ideally we'd parse the hex to RGB for Tailwind opacity support
      root.style.setProperty('--brand-color', t.themeColor);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoadingTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
